import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';

const MyJobs = () => {
  const { toast } = useToast();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobBids, setJobBids] = useState([]);
  const [showBidsModal, setShowBidsModal] = useState(false);
  const [loadingBids, setLoadingBids] = useState(false);

  useEffect(() => {
    loadMyJobs();
  }, []);

  const loadMyJobs = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('dcTech_token') || localStorage.getItem('token');
      
      const response = await fetch(`${backendUrl}/api/my-jobs`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setJobs(data);
      }
    } catch (error) {
      console.error('Error loading my jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadJobBids = async (jobId) => {
    setLoadingBids(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('dcTech_token') || localStorage.getItem('token');
      
      const response = await fetch(`${backendUrl}/api/jobs/${jobId}/bids`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setJobBids(data);
      }
    } catch (error) {
      console.error('Error loading job bids:', error);
    } finally {
      setLoadingBids(false);
    }
  };

  const acceptBid = async (jobId, bidId) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('dcTech_token') || localStorage.getItem('token');
      
      const response = await fetch(`${backendUrl}/api/jobs/${jobId}/bids/${bidId}/accept`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        toast({
          title: "Bid Accepted! 🎉",
          description: "The provider has been notified and your job is now in progress.",
          className: "bg-green-50 border-green-200 text-green-800"
        });

        // Refresh jobs and close modal
        loadMyJobs();
        setShowBidsModal(false);
        setSelectedJob(null);
      } else {
        const errorData = await response.json();
        toast({
          title: "Error",
          description: errorData.detail || "Failed to accept bid",
          className: "bg-red-50 border-red-200 text-red-800"
        });
      }
    } catch (error) {
      console.error('Error accepting bid:', error);
      toast({
        title: "Error",
        description: "Failed to accept bid. Please try again.",
        className: "bg-red-50 border-red-200 text-red-800"
      });
    }
  };

  const formatBudget = (budget, budgetType) => {
    if (!budget) return 'Budget: Negotiable';
    
    const formattedAmount = `$${budget.toLocaleString()}`;
    switch (budgetType) {
      case 'fixed':
        return `Budget: ${formattedAmount} (Fixed)`;
      case 'hourly':
        return `Budget: ${formattedAmount}/hour`;
      default:
        return `Budget: ~${formattedAmount} (Negotiable)`;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'completed':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getTimeAgo = (date) => {
    const now = new Date();
    const posted = new Date(date);
    const diffInHours = Math.floor((now - posted) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just posted';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  const handleViewBids = (job) => {
    setSelectedJob(job);
    setShowBidsModal(true);
    loadJobBids(job.id);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mr-4"></div>
        <span className="text-white">Loading your jobs...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">My Posted Jobs</h2>
        <Button
          onClick={loadMyJobs}
          className="bg-white/20 bg-white/15 text-white border-white/30"
        >
          🔄 Refresh
        </Button>
      </div>

      {jobs.length === 0 ? (
        <Card className="bg-white/10 backdrop-blur-lg border-white/20">
          <CardContent className="text-center py-8">
            <p className="text-white/70 text-lg">You haven't posted any jobs yet.</p>
            <p className="text-white/50 text-sm mt-2">Post your first job to get started!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6">
          {jobs.map((job) => (
            <Card key={job.id} className="bg-white/10 backdrop-blur-lg border-white/20">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-grow">
                    <CardTitle className="text-white text-xl mb-2">{job.title}</CardTitle>
                    <div className="flex items-center space-x-4 text-sm text-white/70">
                      <span>📍 {job.location}</span>
                      <span>🔧 {job.requiredTrade}</span>
                      <span>⏰ Posted {getTimeAgo(job.createdAt)}</span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end space-y-2">
                    <Badge className={getStatusColor(job.status)}>
                      {job.status.toUpperCase().replace('_', ' ')}
                    </Badge>
                    <Badge className="bg-blue-100 text-blue-800 border-blue-200">
                      {job.urgency.toUpperCase()}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-white/80 mb-4">{job.description}</p>
                
                <div className="flex items-center justify-between mb-4">
                  <span className="text-white font-semibold">
                    {formatBudget(job.budget, job.budgetType)}
                  </span>
                  <span className="text-white/70 text-sm">
                    💼 {job.bidsCount} bid{job.bidsCount !== 1 ? 's' : ''}
                  </span>
                </div>

                {/* Job Images */}
                {job.images && job.images.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    {job.images.slice(0, 3).map((image, index) => (
                      <img
                        key={index}
                        src={image}
                        alt={`Job image ${index + 1}`}
                        className="w-full h-20 object-cover rounded"
                      />
                    ))}
                    {job.images.length > 3 && (
                      <div className="w-full h-20 bg-white/20 rounded flex items-center justify-center text-white/70 text-sm">
                        +{job.images.length - 3} more
                      </div>
                    )}
                  </div>
                )}

                <div className="flex space-x-3">
                  {job.bidsCount > 0 ? (
                    <Button
                      onClick={() => handleViewBids(job)}
                      className="bg-gradient-to-r from-purple-500 to-blue-500   text-white"
                    >
                      👁️ View {job.bidsCount} Bid{job.bidsCount !== 1 ? 's' : ''}
                    </Button>
                  ) : (
                    <Button disabled className="bg-gray-500 text-gray-300 cursor-not-allowed">
                      No Bids Yet
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    className="border-white/50 text-white bg-white/10"
                  >
                    ✏️ Edit Job
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Bids Modal */}
      {showBidsModal && selectedJob && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="bg-white max-w-4xl w-full max-h-[80vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="text-gray-800">
                Bids for: {selectedJob.title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingBids ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-4"></div>
                  <span>Loading bids...</span>
                </div>
              ) : jobBids.length === 0 ? (
                <p className="text-center text-gray-600 py-8">No bids yet for this job.</p>
              ) : (
                <div className="space-y-4">
                  {jobBids.map((bid) => (
                    <div key={bid.id} className="border rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-semibold text-lg">{bid.providerName}</h4>
                          <p className="text-gray-600">{bid.providerCompany}</p>
                          {bid.providerRating && (
                            <div className="flex items-center space-x-1 mt-1">
                              <span className="text-yellow-500">⭐</span>
                              <span className="text-sm text-gray-600">{bid.providerRating}</span>
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-green-600">${bid.bidAmount}</p>
                          <p className="text-sm text-gray-500">Duration: {bid.estimatedDuration}</p>
                          <p className="text-sm text-gray-500">Available: {new Date(bid.availableDate).toLocaleDateString()}</p>
                        </div>
                      </div>
                      
                      <p className="text-gray-700 mb-4">{bid.description}</p>
                      
                      <div className="flex items-center justify-between">
                        <Badge className={bid.status === 'accepted' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                          {bid.status.toUpperCase()}
                        </Badge>
                        
                        {bid.status === 'pending' && selectedJob.status === 'open' && (
                          <Button
                            onClick={() => acceptBid(selectedJob.id, bid.id)}
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            ✓ Accept Bid
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="flex justify-end mt-6">
                <Button
                  onClick={() => {
                    setShowBidsModal(false);
                    setSelectedJob(null);
                    setJobBids([]);
                  }}
                  variant="outline"
                >
                  Close
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default MyJobs;